/**
 * Alignment guides while moving or resizing an element (#deck-machine #107, Canva-style): its edges and centre snap to
 * the page's edges, centre and thirds and to every other element's edges and centre, and the lines it snapped to are
 * drawn. All in percent of the page.
 */
export interface GuideBox { x: number; y: number; w: number; h: number }
export interface GuideLines { xs: number[]; ys: number[] }
export interface Snapped { x: number; y: number; v: number[]; h: number[] }

const PAGE = [0, 33.3, 50, 66.7, 100];

/** The lines an element can snap to: the page's, then the other elements' left / centre / right and top / middle / bottom. */
export function guideLines(others: GuideBox[]): GuideLines {
  const xs = new Set<number>(PAGE); const ys = new Set<number>(PAGE);
  for (const o of others) {
    if (o.w >= 99 && o.h >= 99) continue; // a page-size background adds nothing the page lines don't
    for (const v of [o.x, o.x + o.w / 2, o.x + o.w]) xs.add(Math.round(v * 10) / 10);
    for (const v of [o.y, o.y + o.h / 2, o.y + o.h]) ys.add(Math.round(v * 10) / 10);
  }
  return { xs: [...xs], ys: [...ys] };
}

/** The nearest line within `tol` of any of `points` (offset = point - start): how far to move and which line. */
function nearest(points: number[], lines: number[], tol: number): { shift: number; line: number } | null {
  let best: { shift: number; line: number } | null = null;
  for (const p of points) for (const l of lines) { const d = l - p; if (Math.abs(d) <= tol && (!best || Math.abs(d) < Math.abs(best.shift))) best = { shift: d, line: l }; }
  return best;
}

/** A box being moved: its left, centre and right (top, middle, bottom) snap; every line it then touches is a guide. */
export function snapMove(b: GuideBox, lines: GuideLines, tol = 1): Snapped {
  const sx = nearest([b.x, b.x + b.w / 2, b.x + b.w], lines.xs, tol);
  const sy = nearest([b.y, b.y + b.h / 2, b.y + b.h], lines.ys, tol * (16 / 9));
  const x = b.x + (sx?.shift ?? 0); const y = b.y + (sy?.shift ?? 0);
  const on = (pts: number[], ls: number[]) => ls.filter((l) => pts.some((p) => Math.abs(p - l) < 0.05));
  return { x, y, v: sx ? on([x, x + b.w / 2, x + b.w], lines.xs) : [], h: sy ? on([y, y + b.h / 2, y + b.h], lines.ys) : [] };
}

/** One edge being dragged (resize): the line it snaps to, or the edge as it is. */
export function snapEdge(v: number, lines: number[], tol = 1): { v: number; line?: number } {
  const n = nearest([v], lines, tol);
  return n ? { v: n.line, line: n.line } : { v };
}
